/**
 * UPDATES PAGE
 * 
 * Public-facing page showing app updates, announcements, and changelog.
 * Anyone can view, only admins can post (via AdminUpdates page).
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  Megaphone,
  Sparkles,
  Bug,
  Wrench,
  AlertTriangle,
  MessageSquare,
  Loader2,
  ArrowLeft
} from 'lucide-react';
import { PageHeader } from '../components/common';

// Update categories with their icons and colors
const CATEGORIES = {
  announcement: {
    label: 'Announcement',
    icon: Megaphone,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
  },
  feature: {
    label: 'New Feature',
    icon: Sparkles,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
  },
  bugfix: {
    label: 'Bug Fix',
    icon: Bug,
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
  },
  improvement: {
    label: 'Improvement',
    icon: Wrench,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20',
  },
  maintenance: {
    label: 'Maintenance',
    icon: AlertTriangle,
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
  },
};

type CategoryId = keyof typeof CATEGORIES;

interface Update {
  id: string;
  title: string;
  content: string;
  category: CategoryId;
  createdAt: Date;
  isPinned?: boolean;
}

export default function UpdatesPage() {
  const [updates, setUpdates] = useState<Update[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<CategoryId | 'all'>('all');

  // Load updates from Firestore
  useEffect(() => {
    const loadUpdates = async () => {
      try {
        const updatesRef = collection(db, 'updates');
        const q = query(updatesRef, orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        
        const loadedUpdates: Update[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title,
            content: data.content,
            category: data.category,
            createdAt: data.createdAt instanceof Timestamp 
              ? data.createdAt.toDate() 
              : new Date(data.createdAt),
            isPinned: data.isPinned || false,
          };
        });
        
        // Sort: pinned first, then by date
        loadedUpdates.sort((a, b) => {
          if (a.isPinned && !b.isPinned) return -1;
          if (!a.isPinned && b.isPinned) return 1;
          return b.createdAt.getTime() - a.createdAt.getTime();
        });
        
        setUpdates(loadedUpdates);
      } catch (error) {
        console.error('Error loading updates:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUpdates();
  }, []);

  // Filter updates by category
  const filteredUpdates = selectedCategory === 'all' 
    ? updates 
    : updates.filter(u => u.category === selectedCategory);

  // Format date nicely
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-on-bg mb-6 font-body"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <PageHeader
          icon={MessageSquare}
          title="Updates & Announcements"
          subtitle="Stay up to date with the latest features, fixes, and news."
          className="mb-8"
        />

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-6 justify-center">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-default font-body ${
              selectedCategory === 'all' ? 'filter-active' : 'filter-inactive'
            }`}
          >
            All
          </button>
          {Object.entries(CATEGORIES).map(([key, cat]) => {
            const Icon = cat.icon;
            return (
              <button
                key={key}
                onClick={() => setSelectedCategory(key as CategoryId)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-default font-body flex items-center gap-1.5 ${
                  selectedCategory === key ? 'filter-active' : 'filter-inactive'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Updates List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-pink-400 animate-spin" />
          </div>
        ) : filteredUpdates.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <MessageSquare className="w-12 h-12 text-glass-muted mx-auto mb-3" />
            <p className="text-glass-secondary font-body">
              {selectedCategory === 'all' 
                ? 'No updates yet. Check back soon!' 
                : `No ${CATEGORIES[selectedCategory].label.toLowerCase()} updates yet.`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredUpdates.map((update) => {
              const category = CATEGORIES[update.category];
              const Icon = category.icon;
              
              return (
                <article key={update.id} className="glass-card p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${category.bgColor}`}>
                        <Icon className={`w-5 h-5 ${category.color}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="font-bold text-glass font-body">{update.title}</h2>
                          {update.isPinned && (
                            <span className="text-xs bg-pink-500/20 text-pink-400 px-2 py-0.5 rounded-full font-body">
                              Pinned
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`font-medium ${category.color}`}>{category.label}</span>
                          <span className="text-glass-muted">•</span>
                          <span className="text-glass-muted font-body">{formatDate(update.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="text-glass-secondary font-body whitespace-pre-wrap">
                    {update.content}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Discord Link */}
        <div className="mt-8 glass-card p-6 text-center">
          <p className="text-glass-secondary font-body mb-3">
            Want to discuss updates or share feedback?
          </p>
          <a
            href="https://discord.gg/QwDyUDwHdz"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#5865F2] text-white rounded-lg font-medium hover:bg-[#4752C4] transition-default font-body"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            Join our Discord
          </a>
        </div>
      </div>
    </div>
  );
}
